import { Capacitor } from "@capacitor/core";
// eslint-disable-next-line no-restricted-imports -- the DOM listener / timer / outbox wiring below is a genuinely required effect (see its use-effect-allowed rationale)
import { type ReactElement, useEffect, useRef } from "react";
import { useCurrentUser } from "@client/hooks/use-current-user";
import {
  impersonationRef,
  useImpersonationRefValue,
} from "@client/ImperativeState";
import { BUILD_ID_QUERY_OPTIONS } from "@client/components/utils/OutOfDateVersionWarning";
import { isIPad } from "@client/lib/isIPad";
import { trpc } from "@client/trpc";
import { generatePresenceSegmentId } from "@lib/utils/idGeneration";
import { PRESENCE_CHECKPOINT_MS } from "@lib/usageMetrics/constants";
import {
  createPresenceBuilder,
  type PresenceInputKind,
} from "./presenceSegments";
import { createPresenceSync, toPresencePayload } from "./presenceSync";

/** Window events that mean the person is deliberately working. */
const DELIBERATE_WINDOW_EVENTS = [
  "pointerdown",
  "keydown",
  "wheel",
  "scroll",
  "touchstart",
  "touchmove",
] as const;

/** Media progress counts too: hands-off watching is active attention. */
const DELIBERATE_DOCUMENT_EVENTS = ["playing", "timeupdate"] as const;

const LAST_KNOWN_BUILD_ID_KEY = "presence.lastKnownBuildId";

function readLastKnownBuildId(): string | null {
  try {
    return globalThis.localStorage?.getItem(LAST_KNOWN_BUILD_ID_KEY) ?? null;
  } catch {
    return null;
  }
}

function writeLastKnownBuildId(value: string): void {
  try {
    globalThis.localStorage?.setItem(LAST_KNOWN_BUILD_ID_KEY, value);
  } catch {
    // A full or disabled store costs nothing here: the next launch simply
    // starts from "unknown" again until the server answers.
  }
}

function currentDevice(): "ipad" | "iphone" | undefined {
  // `isIPad()` is true for the native iPad shell AND an iPad browser; the
  // iPhone label is native-only, since an iPhone browser is not a shell we
  // ship. The server refines the rest from the User-Agent.
  if (isIPad()) {
    return "ipad";
  }
  return Capacitor.isNativePlatform() ? "iphone" : undefined;
}

/**
 * The web shells' recorder: identity from the shared current-user query. The
 * iPhone shell mounts `IPhonePresenceRecorder` instead, whose identity is the
 * shell's own (cached while offline) — this query is disabled there while
 * offline and would leave the recorder idle for a whole offline sitting.
 */
export function PresenceRecorder({
  checkpointMs = PRESENCE_CHECKPOINT_MS,
}: {
  /** Test seam; production uses the shared constant. */
  checkpointMs?: number;
} = {}): ReactElement {
  const { data: currentUser } = useCurrentUser();
  return (
    <PresenceRecorderCore
      userId={currentUser?.userId ?? null}
      checkpointMs={checkpointMs}
    />
  );
}

/**
 * Turns this page's visibility and input into presence segments and hands
 * them to a durable outbox. A null leaf mounted once per app shell, behind a
 * wrapper that supplies the shell's effective identity.
 *
 * Records nothing while logged out or while an SFC admin is in View-As: that
 * time is neither the impersonated funder's presence nor useful admin
 * presence (the same rule watch segments follow). The server enforces it
 * independently — a write arriving under an acting-as header is a no-op.
 */
export type PresenceInputSubscribe = (
  note: (kind: PresenceInputKind) => void,
) => () => void;

export function PresenceRecorderCore({
  userId: realUserId,
  checkpointMs = PRESENCE_CHECKPOINT_MS,
  subscribeInput,
}: {
  /** The real (underlying) user, or null while logged out. */
  userId: string | null;
  /** Test seam; production uses the shared constant. */
  checkpointMs?: number;
  /**
   * Input that never reaches the DOM — the iPhone shell's native video
   * playback — is fed in through here: called once per mounted segment
   * source with the builder's note function, returns the unsubscribe. Must
   * be a stable reference (a module-level function): it is an effect
   * dependency, and a new identity per render would tear the recorder down
   * mid-segment.
   */
  subscribeInput?: PresenceInputSubscribe;
}): null {
  const viewAsUserId = useImpersonationRefValue();

  // The build id resolves after mount and refetches periodically
  // (OutOfDateVersionWarning owns the cached query). Read it through a
  // render-assigned ref rather than an effect dependency: as a dep, every
  // resolve or refetch would tear the recorder down mid-segment and open a
  // new one. The FIRST answer is kept for the life of this page, so a deploy
  // while the tab stays open does not relabel its later segments. This is the
  // server's build at page load, not a hash baked into the bundle (none
  // exists): it names the client's own build except when the code running
  // here is older than the server it first spoke to — a native bundle
  // installed before a deploy, or a cached web asset — which is the accepted
  // narrowing recorded in the spec's `buildId` contract.
  const { data: buildId } = trpc.getBuildId.useQuery(
    undefined,
    BUILD_ID_QUERY_OPTIONS,
  );
  // Until the server answers, the last build this device saw stands in: on
  // an offline cold launch the query cannot resolve at all, and a segment
  // written as "unknown" keeps that label for good (later checkpoints grow
  // the row's end and evidence, never its build). The server's first answer
  // replaces it — the page just loaded, so the server's build is this
  // page's build — and is remembered for the next launch.
  const buildIdRef = useRef<{ value: string | null; fromServer: boolean }>({
    value: readLastKnownBuildId(),
    fromServer: false,
  });
  if (!buildIdRef.current.fromServer && buildId != null) {
    buildIdRef.current = { value: buildId.repoCommitHash, fromServer: true };
    writeLastKnownBuildId(buildId.repoCommitHash);
  }

  // use-effect-allowed -- syncs browser visibility/input events and a durable queue
  useEffect(() => {
    if (viewAsUserId != null || realUserId == null) {
      return;
    }

    const feature = createPresenceSync({
      userId: realUserId,
      isNative: Capacitor.isNativePlatform(),
      // A send in flight when the user switches into impersonation goes out
      // under acting-as headers; the server no-ops it and an ungated flush
      // would dequeue the real user's write as if delivered. Checked at
      // commit time against the LIVE ref (the closure's viewAsUserId is the
      // mount-time value, stale by definition when the flip happens).
      shouldCommit: () => impersonationRef.current == null,
    });

    const builder = createPresenceBuilder({
      now: () => Date.now(),
      generateSegmentId: generatePresenceSegmentId,
      // The builder's clock-jump gate is expressed in checkpoints, so it must
      // agree with the interval below or a normal tick reads as a sleep.
      checkpointMs,
      onSegment: (segment) => {
        feature.enqueue(
          toPresencePayload(segment, {
            native: Capacitor.isNativePlatform(),
            device: currentDevice(),
            buildId: buildIdRef.current.value ?? "unknown",
          }),
        );
      },
    });

    const noteDeliberate = () => builder.noteInput("deliberate");
    const notePointer = () => builder.noteInput("pointer");
    const onVisibility = () =>
      builder.setVisible(document.visibilityState === "visible");
    const onPageHide = (event: PageTransitionEvent) => {
      // bfcache: the page may resume, so keep the segment. The hidden
      // visibilitychange that precedes this has already checkpointed. Native
      // shells never unload; their background transition is visibilitychange.
      if (!event.persisted && !Capacitor.isNativePlatform()) {
        builder.close();
      }
    };

    // Passive capture everywhere: a pointermove storm must cost O(1) per
    // event and must never delay the page's own handling of it.
    const passiveCapture = { capture: true, passive: true } as const;
    window.addEventListener("pointermove", notePointer, passiveCapture);
    for (const name of DELIBERATE_WINDOW_EVENTS) {
      window.addEventListener(name, noteDeliberate, passiveCapture);
    }
    // Media events don't bubble, so the document listener must capture.
    for (const name of DELIBERATE_DOCUMENT_EVENTS) {
      document.addEventListener(name, noteDeliberate, passiveCapture);
    }
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onPageHide);

    const interval = setInterval(() => builder.tick(), checkpointMs);
    const unsubscribeInput = subscribeInput?.((kind) =>
      builder.noteInput(kind),
    );

    feature.start();
    builder.setVisible(document.visibilityState === "visible");

    return () => {
      unsubscribeInput?.();
      // Close FIRST: the final enqueue must land on the still-durable queue
      // before the worker stops (a stopped feature's queue still persists, so
      // the next started worker delivers it) — the ordering rule the video
      // provider documents.
      builder.close();
      window.removeEventListener("pointermove", notePointer, passiveCapture);
      for (const name of DELIBERATE_WINDOW_EVENTS) {
        window.removeEventListener(name, noteDeliberate, passiveCapture);
      }
      for (const name of DELIBERATE_DOCUMENT_EVENTS) {
        document.removeEventListener(name, noteDeliberate, passiveCapture);
      }
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
      clearInterval(interval);
      feature.stop();
    };
  }, [realUserId, viewAsUserId, checkpointMs, subscribeInput]);

  return null;
}
